import {
  CONVERSATION_PRIORITY_ORDER,
  MESSAGE_TYPE,
} from 'shared/constants/messages';
import { applyPageFilters } from './helpers';
import store from '../../../store';
import conversation from '../../../api/inbox/conversation';

function compareValues(attributeValue, condition, comparisonValue) {
  switch (condition) {
    case '>':
      return attributeValue > comparisonValue;
    case '>=':
      return attributeValue >= comparisonValue;
    case '<':
      return attributeValue < comparisonValue;
    case '<=':
      return attributeValue <= comparisonValue;
    case '==':
      return attributeValue === comparisonValue;
    case '!=':
      return attributeValue !== comparisonValue;
    default:
      return false;
  }
}

export const getSelectedChatConversation = ({
  allConversations,
  selectedChatId,
}) =>
  allConversations.filter(conversation => conversation.id === selectedChatId);

// getters
const getters = {
  getReadStatusFilter: state => state.readStatusFilter,
  
  getAllConversations: ({ allConversations, chatSortFilter }) => {
    const comparator = {
      latest: (a, b) => b.last_activity_at - a.last_activity_at,
      sort_on_created_at: (a, b) => a.created_at - b.created_at,
      sort_on_priority: (a, b) => {
        return (
          CONVERSATION_PRIORITY_ORDER[a.priority] -
          CONVERSATION_PRIORITY_ORDER[b.priority]
        );
      },
      sort_on_classification: (a, b) => {
        const groupAttributes = store.getters['attributes/getGroupAttributes'];
        const comparator = Array.isArray(a.contact_classification) ? a.contact_classification.reduce(
          (acc, item) => {
            if (item.attribute_type === a.classification_type) {
              acc.push(item);
            }
            return acc;
          },
          []
        ) : [];
      
        const findClassification = (conversation) => {
          if (conversation.classification_type === 'group_attributes') {
            const classification = groupAttributes[0]?.group_attributes?.find(
              (item) => item.id === conversation.contact_workplace
            );
            if (classification) {
              for (const item of comparator) {
                let attributeValue = classification[item.attribute_name];
                const condition = item.condition;
                let comparisonValue = item.value;
                const valueType = groupAttributes[0][item.attribute_name].type
                if (valueType != 'text') {
                  attributeValue = Number(attributeValue)/100;
                }
                if (!isNaN(Number(comparisonValue))) {
                  comparisonValue = Number(comparisonValue);
                }
                if (compareValues(attributeValue, condition, comparisonValue)) {
                  return item?.priority ?? Number.MAX_SAFE_INTEGER;
                }
              }
            }
      
          } else {
            const customAttributes = conversation.contact_attributes;
            
            for (const item of comparator) {
              const attributeName = item.attribute_name;
      
              if (customAttributes.hasOwnProperty(attributeName)) {
                let attributeValue = customAttributes[attributeName];
                const condition = item.condition;
                let comparisonValue = item.value;

                if (!isNaN(Number(attributeValue))) {
                  attributeValue = Number(attributeValue);
                }
                if (!isNaN(Number(comparisonValue))) {
                  comparisonValue = Number(comparisonValue);
                }
                if (compareValues(attributeValue, condition, comparisonValue)) {
                  return item?.priority ?? Number.MAX_SAFE_INTEGER;
                }
              }
            }
          }
        };
      
        const getPriorityA = findClassification(a);
        const priorityA = getPriorityA ?? Number.MAX_SAFE_INTEGER;
        const getPriorityB = findClassification(b);
        const priorityB = getPriorityB ?? Number.MAX_SAFE_INTEGER;
      
        return (priorityA) - (priorityB); 
      }      
    };

    return allConversations.sort(comparator[chatSortFilter]);
  },
  getSelectedChat: ({ selectedChatId, allConversations }) => {
    const selectedChat = allConversations.find(
      conversation => conversation.id === selectedChatId
    );
    return selectedChat || {};
  },
  getSelectedChatAttachments: (_state, _getters) => {
    const selectedChat = _getters.getSelectedChat;
    const { attachments } = selectedChat;
    return attachments;
  },
  getLastEmailInSelectedChat: (stage, _getters) => {
    const selectedChat = _getters.getSelectedChat;
    const { messages = [] } = selectedChat;
    const lastEmail = [...messages].reverse().find(message => {
      const {
        content_attributes: contentAttributes = {},
        message_type: messageType,
      } = message;
      const { email = {} } = contentAttributes;
      const isIncomingOrOutgoing =
        messageType === MESSAGE_TYPE.OUTGOING ||
        messageType === MESSAGE_TYPE.INCOMING;
      if (email.from && isIncomingOrOutgoing) {
        return true;
      }
      return false;
    });

    return lastEmail;
  },
  getMineChats: (_state, _, __, rootGetters) => activeFilters => {
    const currentUserID = rootGetters.getCurrentUser?.id;

    return _state.allConversations.filter(conversation => {
      const { assignee, sender } = conversation.meta;
      const isAssignedToMe =
        (sender && sender.agent_id === currentUserID) ||
        (assignee && assignee.id === currentUserID) ||
        (activeFilters?.contact_id &&
          activeFilters?.contact_id === conversation.contact_id);
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      const isChatMine = isAssignedToMe && shouldFilter;

      return isChatMine;
    });
  },
  getAppliedConversationFilters: _state => {
    return _state.appliedFilters;
  },
  getStatusFilter: _state => {
    return _state.statusFilterConversationAndContact;
  },
  getUnAssignedChats: _state => (
    activeFilters,
    permissions = null,
    isAgent = null
  ) => {
    return _state.allConversations.filter(conversation => {
      const isUnAssigned = !conversation.meta.assignee;
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      const isPartOfMyTeam = conversation?.meta?.team?.is_member !== false;

      if (permissions || isAgent) {
        return isPartOfMyTeam && isUnAssigned && shouldFilter;
      }
      return isUnAssigned && shouldFilter;
    });
  },
  getAllStatusChats: _state => (
    activeFilters,
    permissions = null,
    currentId = null
  ) => {
    return _state.allConversations.filter(conversation => {
      const assigneeID =
        conversation?.last_non_activity_message?.conversation?.assignee_id;
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      const isPartOfMyTeam = conversation?.meta?.team?.is_member !== false;

      if (permissions) {
        return (
          shouldFilter &&
          (isPartOfMyTeam || assigneeID === currentId || assigneeID === null)
        );
      }
      return shouldFilter;
    });
  },
  getChatListLoadingStatus: ({ listLoadingStatus }) => listLoadingStatus,
  getAllMessagesLoaded(_state) {
    const [chat] = getSelectedChatConversation(_state);
    return !chat || chat.allMessagesLoaded === undefined
      ? false
      : chat.allMessagesLoaded;
  },
  getUnreadCount(_state) {
    const [chat] = getSelectedChatConversation(_state);
    if (!chat) return [];
    return chat.messages.filter(
      chatMessage =>
        chatMessage.created_at * 1000 > chat.agent_last_seen_at * 1000 &&
        chatMessage.message_type === 0 &&
        chatMessage.private !== true
    ).length;
  },
  getChatStatusFilter: ({ chatStatusFilter }) => chatStatusFilter,
  getChatSortFilter: ({ chatSortFilter }) => chatSortFilter,
  getChatClassificationFilter: ({ chatClassificationFilter }) => chatClassificationFilter,
  getChatClassification: _state => {
    const activeInbox = store.getters['getSelectedInbox']
    const inbox = store.getters['inboxes/getInbox'](activeInbox);
    const { classification_type, contact_classification } = inbox;
    const filteredClassifications = contact_classification.filter(
      item => item.attribute_type === classification_type
    );
    return filteredClassifications.map(item => item.classification);
  },  
  getSelectedInbox: ({ currentInbox }) => currentInbox,
  getConversationById: _state => conversationId => {
    return _state.allConversations.find(
      value => value.id === Number(conversationId)
    );
  },
  getConversationParticipants: _state => {
    return _state.conversationParticipants;
  },
  getConversationLastSeen: _state => {
    return _state.conversationLastSeen;
  },
};

export default getters;
